export const emailPrefix = (email) =>
  email?.includes("@") ? email.split("@")[0] : (email ?? "");

export const getInitials = (str) => {
  if (!str) return "??";
  const name = str.includes("@") ? emailPrefix(str) : str;
  const parts = name
    .trim()
    .split(/[\s.\-_]+/)
    .filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

// Shows "+ N" for the other people; a solo application shows only its title
export const getApplicationTitle = (app, t) => {
  const others = (app.participants ?? []).length - 1;
  return others > 0 ? `${app.title} + ${others}` : app.title;
};

export const resolveParticipantName = (
  participant,
  deletedUserLabel = "Deleted user"
) => {
  if (participant?.is_deleted) return deletedUserLabel;
  if (participant.name) return participant.name;
  const id = participant.id;
  if (id && typeof id === "object") {
    const fname = id.fname || id.firstname || "";
    const lname = id.lname || id.lastname || "";
    const full = `${fname} ${lname}`.trim();
    if (full) return full;
  }
  return emailPrefix(participant.email ?? "");
};

export const toIdString = (value) => (value ? String(value?._id ?? value) : "");

/* Guarantors (is_guarantor with guarantor_of) go right after the tenant they
   back. Guarantors whose tenant is not in the list keep their place. */
export const sortParticipantsWithGuarantors = (participants) => {
  const participantIds = new Set(
    participants.map((p) => toIdString(p.id)).filter(Boolean)
  );
  const isPlacedGuarantor = (p) =>
    p.is_guarantor === true &&
    participantIds.has(toIdString(p.guarantor_of)) &&
    toIdString(p.guarantor_of) !== toIdString(p.id);
  return participants.flatMap((p) => {
    if (isPlacedGuarantor(p)) return [];
    const id = toIdString(p.id);
    const guarantors = id
      ? participants.filter(
          (g) => isPlacedGuarantor(g) && toIdString(g.guarantor_of) === id
        )
      : [];
    return [p, ...guarantors];
  });
};

/* Whether the invited user can accept as the creator's guarantor in this
   application. Not when they are already a guarantor here, have guarantors
   here, or the creator is a guarantor here (no guarantor of a guarantor). */
export const canJoinAsGuarantor = (app, user) => {
  const userId = toIdString(user?._id);
  const userEmail = user?.email?.toLowerCase();
  const creatorId = toIdString(app?.created_by);
  if (!creatorId || creatorId === userId) return false;
  const active = (app?.participants ?? []).filter((p) => !p?.is_deleted);
  const ownRow = active.find((p) => p?.email?.toLowerCase() === userEmail);
  if (ownRow?.is_guarantor === true) return false;
  const hasGuarantorsHere =
    !!userId &&
    active.some(
      (p) => p?.is_guarantor === true && toIdString(p.guarantor_of) === userId
    );
  const creatorIsGuarantor = active.some(
    (p) => p?.is_guarantor === true && toIdString(p.id) === creatorId
  );
  return !hasGuarantorsHere && !creatorIsGuarantor;
};
